import React, { useState, Children, lazy } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRightIcon, TruckIcon, ShieldCheckIcon, RotateCcwIcon, PhoneIcon, SendIcon, MailIcon, ChevronDownIcon, Waves, Droplets, TestTube, Settings, Paintbrush, Workflow, Shield, Package, Layers, Activity, Flame, Wrench, ChevronRightIcon } from 'lucide-react';
import { Button } from '../components/ui/Button';
import { ProductCard } from '../components/product/ProductCard';
import { Input } from '../components/ui/Input';
import { categories } from '../data/categories';
import { products } from '../data/products';
import { blogPosts, faqItems } from '../data/blog';
const containerVariants = {
  hidden: {
    opacity: 0
  },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.05
    }
  }
};
const itemVariants = {
  hidden: {
    opacity: 0,
    y: 20
  },
  visible: {
    opacity: 1,
    y: 0
  }
};
const categoryIcons: Record<string, any> = {
  pools: Waves,
  jacuzzi: Droplets,
  chemicals: TestTube,
  equipment: Settings,
  finishing: Paintbrush,
  pipes: Workflow,
  covers: Shield,
  accessories: Package,
  embedded: Layers,
  aquafitness: Activity,
  sauna: Flame,
  'spare-parts': Wrench
};
export function HomePage() {
  const [openFaq, setOpenFaq] = useState<string | null>(null);
  const [consultationForm, setConsultationForm] = useState({
    name: '',
    phone: '',
    email: ''
  });
  const bestSellers = products.filter(p => p.badge === 'Хіт продажів').slice(0, 4);
  const newProducts = products.filter(p => p.badge === 'Новинка').slice(0, 4);
  const saleProducts = products.filter(p => p.oldPrice).slice(0, 4);
  const handleConsultationSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    alert("Дякуємо! Ми зв'яжемося з вами найближчим часом.");
    setConsultationForm({
      name: '',
      phone: '',
      email: ''
    });
  };
  return <main className="bg-[#0a0a0a]" data-id="element-836">
      {/* Hero Section */}
      <section className="relative overflow-hidden bg-[#0a0a0a]" data-id="element-837">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-838">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-center py-12 lg:py-20" data-id="element-839">
            <motion.div initial={{
            opacity: 0,
            x: -30
          }} animate={{
            opacity: 1,
            x: 0
          }} transition={{
            duration: 0.6
          }} className="order-2 lg:order-1 text-center lg:text-left" data-id="element-840">
              <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white leading-tight mb-4 lg:mb-6" data-id="element-841">
                Басейни та обладнання для{' '}
                <span className="text-gray-400" data-id="element-842">чистої води</span> та комфорту
              </h1>
              <h2 className="text-lg sm:text-xl text-gray-400 mb-6 lg:mb-8" data-id="element-843">
                Aqua Pools (Івано-Франківськ) — підбір, доставка та сервіс.
                Допоможемо зібрати комплект під ваш басейн.
              </h2>
              <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start" data-id="element-844">
                <Link to="/catalog" className="w-full sm:w-auto" data-id="element-845">
                  <Button size="lg" className="w-full sm:w-auto" rightIcon={<ArrowRightIcon className="w-5 h-5" data-id="element-847" />} data-id="element-846">
                    Перейти в каталог
                  </Button>
                </Link>
                <Link to="/contacts" className="w-full sm:w-auto" data-id="element-848">
                  <Button variant="outline" size="lg" className="w-full sm:w-auto" data-id="element-849">
                    Контакти
                  </Button>
                </Link>
              </div>
            </motion.div>

            <motion.div initial={{
            opacity: 0,
            x: 30
          }} animate={{
            opacity: 1,
            x: 0
          }} transition={{
            duration: 0.6,
            delay: 0.2
          }} className="order-1 lg:order-2" data-id="element-850">
              <div className="relative aspect-[4/3] lg:aspect-square rounded-2xl overflow-hidden shadow-2xl shadow-black/50" data-id="element-851">
                <img src="https://cdn.magicpatterns.com/uploads/rADafJCbbtvX3awmsyEfK8/raimo-lantelankallio-PG9c6rWUpVM-unsplash.jpg" alt="Басейн" className="w-full h-full object-cover" data-id="element-852" />
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-12 lg:py-16 bg-[#0a0a0a]" data-id="element-853">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-854">
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true
        }} variants={containerVariants} data-id="element-855">
            <div className="text-center mb-10 lg:mb-12" data-id="element-856">
              <motion.h2 variants={itemVariants} className="text-2xl sm:text-3xl lg:text-4xl font-bold text-white mb-3" data-id="element-857">
                Чому обирають нас?
              </motion.h2>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8" data-id="element-858">
              {[{
              icon: TruckIcon,
              title: 'Швидка доставка',
              text: 'Відправляємо замовлення в день оформлення. Доставка по всій Україні протягом 1-3 днів.'
            }, {
              icon: ShieldCheckIcon,
              title: 'Гарантія на товар',
              text: 'Офіційна гарантія виробника на все обладнання від 12 до 36 місяців. Якість підтверджена.'
            }, {
              icon: RotateCcwIcon,
              title: 'Швидке повернення',
              text: 'Повернення товару протягом 14 днів без зайвих питань. Ваш комфорт — наш пріоритет.'
            }].map((item, index) => <motion.div key={index} variants={itemVariants} className="bg-[#1a1a1a]/50 backdrop-blur-sm border border-[#2a2a2a] rounded-2xl p-6 lg:p-8 hover:bg-[#1e1e1e]/70 transition-all hover:shadow-lg hover:shadow-white/5" data-id="element-859">
                  <div className="w-14 h-14 bg-white/10 rounded-xl flex items-center justify-center mb-4" data-id="element-860">
                    <item.icon className="w-7 h-7 text-white" data-id="element-861" />
                  </div>
                  <h3 className="text-xl font-semibold text-white mb-3" data-id="element-862">
                    {item.title}
                  </h3>
                  <p className="text-gray-400 leading-relaxed" data-id="element-863">{item.text}</p>
                </motion.div>)}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Best Sellers */}
      <section className="py-12 lg:py-16 bg-[#0a0a0a]" data-id="element-864">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-865">
          <div className="text-center mb-10 lg:mb-12" data-id="element-866">
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-white" data-id="element-867">
              Хіт продажів
            </h2>
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-6" data-id="element-868">
            {bestSellers.map(product => <ProductCard key={product.id} product={product} data-id="element-869" />)}
          </div>
          <div className="text-center mt-8 lg:mt-10" data-id="element-870">
            <Link to="/catalog" data-id="element-871">
              <Button variant="outline" size="lg" rightIcon={<ArrowRightIcon className="w-4 h-4" data-id="element-873" />} data-id="element-872">
                Дивитись всі
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* New Products */}
      <section className="py-12 lg:py-16 bg-[#0a0a0a]" data-id="element-874">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-875">
          <div className="text-center mb-10 lg:mb-12" data-id="element-876">
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-white" data-id="element-877">
              Новинки
            </h2>
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-6" data-id="element-878">
            {newProducts.map(product => <ProductCard key={product.id} product={product} data-id="element-879" />)}
          </div>
          <div className="text-center mt-8 lg:mt-10" data-id="element-880">
            <Link to="/catalog" data-id="element-881">
              <Button variant="outline" size="lg" rightIcon={<ArrowRightIcon className="w-4 h-4" data-id="element-883" />} data-id="element-882">
                Дивитись всі
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Sale Products */}
      <section className="py-12 lg:py-16 bg-[#0a0a0a]" data-id="element-884">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-885">
          <div className="text-center mb-10 lg:mb-12" data-id="element-886">
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-white" data-id="element-887">
              Розпродаж
            </h2>
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-6" data-id="element-888">
            {saleProducts.map(product => <ProductCard key={product.id} product={product} data-id="element-889" />)}
          </div>
          <div className="text-center mt-8 lg:mt-10" data-id="element-890">
            <Link to="/catalog" data-id="element-891">
              <Button variant="outline" size="lg" rightIcon={<ArrowRightIcon className="w-4 h-4" data-id="element-893" />} data-id="element-892">
                Дивитись всі
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Categories */}
      <section className="py-12 lg:py-16 bg-[#0a0a0a]" data-id="element-894">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-895">
          <div className="text-center mb-10 lg:mb-12" data-id="element-896">
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-white mb-3" data-id="element-897">
              Категорії товарів
            </h2>
            <p className="text-lg text-gray-400" data-id="element-898">
              Все необхідне для басейну, сауни та відпочинку на воді
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4" data-id="element-899">
            {categories.map((category, index) => {
            const Icon = categoryIcons[category.id] || Package;
            return <motion.div key={category.id} initial={{
              opacity: 0,
              y: 20
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              delay: index * 0.05
            }} data-id="element-900">
                  <Link to={`/category/${category.slug}`} className="group relative block h-full" data-id="element-901">
                    <div className="relative bg-[#1a1a1a]/40 backdrop-blur-md border border-[#2a2a2a]/50 rounded-2xl p-7 overflow-hidden transition-all duration-300 hover:bg-[#1e1e1e]/60 hover:border-white/10 h-full min-h-[120px]" data-id="element-902">
                      <div className="absolute inset-0 bg-gradient-to-br from-white/[0.03] to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" data-id="element-903" />

                      <div className="relative flex items-center gap-6 h-full" data-id="element-904">
                        <div className="w-16 h-16 rounded-xl bg-[#1a1a1a] border border-[#2a2a2a] flex items-center justify-center group-hover:bg-white/10 group-hover:border-white/20 transition-all duration-300 flex-shrink-0" data-id="element-905">
                          <Icon className="w-8 h-8 text-gray-500 group-hover:text-white transition-colors duration-300" data-id="element-906" />
                        </div>

                        <div className="flex-1 min-w-0" data-id="element-907">
                          <h3 className="text-xl font-semibold text-white group-hover:text-gray-100 transition-colors duration-300 mb-1.5" data-id="element-908">
                            {category.name}
                          </h3>
                          <p className="text-sm text-gray-500" data-id="element-909">
                            {category.productCount} товарів
                          </p>
                        </div>

                        <ChevronRightIcon className="w-6 h-6 text-gray-600 group-hover:text-white group-hover:translate-x-1 transition-all duration-300 flex-shrink-0" data-id="element-910" />
                      </div>
                    </div>
                  </Link>
                </motion.div>;
          })}
          </div>
        </div>
      </section>

      {/* Map & Contacts */}
      <section className="py-12 lg:py-16 bg-[#0a0a0a]" data-id="element-911">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-912">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12" data-id="element-913">
            <div className="rounded-2xl overflow-hidden shadow-lg h-80 lg:h-96 bg-[#1a1a1a] border border-[#2a2a2a]" data-id="element-914">
              <iframe src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2598.8947845935634!2d24.709722315743!3d48.92267997929!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x4730c16c34d5e0e3%3A0x4a0d9b0e4e4e4e4e!2z0IbQstCw0L3Qvi3QpNGA0LDQvdC60ZbQstGB0YzQuiwg0IbQstCw0L3Qvi3QpNGA0LDQvdC60ZbQstGB0YzQutCwINC-0LHQu9Cw0YHRgtGMLCDQo9C60YDQsNGX0L3QsA!5e0!3m2!1suk!2sua!4v1234567890123!5m2!1suk!2sua" width="100%" height="100%" style={{
              border: 0,
              filter: 'grayscale(100%) invert(92%) contrast(83%)'
            }} allowFullScreen loading="lazy" referrerPolicy="no-referrer-when-downgrade" title="Карта Івано-Франківська" data-id="element-915"></iframe>
            </div>

            <div className="flex flex-col justify-center" data-id="element-916">
              <h2 className="text-2xl sm:text-3xl font-bold text-white mb-6" data-id="element-917">
                Контакти
              </h2>
              <div className="space-y-4" data-id="element-918">
                {[{
                icon: PhoneIcon,
                label: 'Телефон',
                value: '+38 (067) 123-45-67',
                href: 'tel:+380671234567'
              }, {
                icon: MailIcon,
                label: 'Email',
                value: 'sales@aquapools.ua',
                href: 'mailto:sales@aquapools.ua'
              }, {
                icon: SendIcon,
                label: 'Telegram',
                value: '@aquapools_if',
                href: 'https://t.me/aquapools_if'
              }].map((item, index) => <a key={index} href={item.href} target={item.href.startsWith('http') ? '_blank' : undefined} rel={item.href.startsWith('http') ? 'noopener noreferrer' : undefined} className="flex items-center gap-4 p-4 bg-[#1a1a1a] border border-[#2a2a2a] rounded-xl hover:bg-[#1e1e1e] hover:border-white/10 transition-all" data-id="element-919">
                    <div className="w-12 h-12 bg-white/10 rounded-xl flex items-center justify-center flex-shrink-0" data-id="element-920">
                      <item.icon className="w-6 h-6 text-white" data-id="element-921" />
                    </div>
                    <div data-id="element-922">
                      <p className="text-sm text-gray-500" data-id="element-923">{item.label}</p>
                      <p className="font-semibold text-white" data-id="element-924">{item.value}</p>
                    </div>
                  </a>)}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-12 lg:py-16 bg-[#0a0a0a]" data-id="element-925">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-926">
          <div className="text-center mb-10 lg:mb-12" data-id="element-927">
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-white mb-3" data-id="element-928">
              Часті питання
            </h2>
            <p className="text-lg text-gray-400" data-id="element-929">
              Відповіді на популярні питання наших клієнтів
            </p>
          </div>

          <div className="space-y-3" data-id="element-930">
            {faqItems.slice(0, 6).map(faq => <div key={faq.id} className="bg-[#1a1a1a] border border-[#2a2a2a] rounded-xl overflow-hidden" data-id="element-931">
                <button onClick={() => setOpenFaq(openFaq === faq.id ? null : faq.id)} className="w-full flex items-center justify-between p-5 text-left hover:bg-[#1e1e1e] transition-colors" data-id="element-932">
                  <span className="font-medium text-white pr-4" data-id="element-933">
                    {faq.question}
                  </span>
                  <ChevronDownIcon className={`w-5 h-5 text-gray-500 flex-shrink-0 transition-transform ${openFaq === faq.id ? 'rotate-180' : ''}`} data-id="element-934" />
                </button>
                {openFaq === faq.id && <div className="px-5 pb-5 pt-0 border-t border-[#2a2a2a]" data-id="element-935">
                    <p className="text-gray-400 pt-4" data-id="element-936">{faq.answer}</p>
                  </div>}
              </div>)}
          </div>

          <div className="text-center mt-8" data-id="element-937">
            <Link to="/faq" data-id="element-938">
              <Button variant="outline" rightIcon={<ArrowRightIcon className="w-4 h-4" data-id="element-940" />} data-id="element-939">
                Всі питання
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Blog */}
      <section className="py-12 lg:py-16 bg-[#0a0a0a]" data-id="element-941">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-942">
          <div className="text-center mb-10 lg:mb-12" data-id="element-943">
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-white mb-3" data-id="element-944">
              Блог та корисні поради
            </h2>
            <p className="text-lg text-gray-400" data-id="element-945">
              Ділимося досвідом догляду за басейном та вибору обладнання
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8" data-id="element-946">
            {blogPosts.slice(0, 3).map(post => <article key={post.id} className="bg-[#1a1a1a] border border-[#2a2a2a] rounded-2xl overflow-hidden hover:border-white/10 transition-all duration-300 flex flex-col" data-id="element-947">
                <Link to={`/blog/${post.id}`} className="block" data-id="element-948">
                  <div className="aspect-[16/10] overflow-hidden bg-[#0a0a0a]" data-id="element-949">
                    <img src={post.image} alt={post.title} className="w-full h-full object-cover hover:scale-105 transition-transform duration-500" data-id="element-950" />
                  </div>
                </Link>
                <div className="p-5 flex flex-col flex-1" data-id="element-951">
                  <div className="flex items-center gap-3 text-sm text-gray-500 mb-3" data-id="element-952">
                    <span data-id="element-953">{post.date}</span>
                    <span data-id="element-954">•</span>
                    <span data-id="element-955">{post.readTime}</span>
                  </div>
                  <Link to={`/blog/${post.id}`} data-id="element-956">
                    <h3 className="font-semibold text-white mb-2 hover:text-gray-300 transition-colors line-clamp-2" data-id="element-957">
                      {post.title}
                    </h3>
                  </Link>
                  <p className="text-sm text-gray-400 line-clamp-2 mb-4 flex-1" data-id="element-958">
                    {post.excerpt}
                  </p>
                  <Link to={`/blog/${post.id}`} className="mt-auto" data-id="element-959">
                    <Button variant="outline" size="sm" className="w-full" rightIcon={<ArrowRightIcon className="w-4 h-4" data-id="element-961" />} data-id="element-960">
                      Читати статтю
                    </Button>
                  </Link>
                </div>
              </article>)}
          </div>

          <div className="text-center mt-8 lg:mt-10" data-id="element-962">
            <Link to="/blog" data-id="element-963">
              <Button variant="outline" size="lg" rightIcon={<ArrowRightIcon className="w-4 h-4" data-id="element-965" />} data-id="element-964">
                Всі статті
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Consultation Form */}
      <section className="py-12 lg:py-16 bg-[#0a0a0a]" data-id="element-966">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-967">
          <div className="text-center mb-8 lg:mb-10" data-id="element-968">
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-white mb-3" data-id="element-969">
              Замовити консультацію
            </h2>
            <p className="text-lg text-gray-400" data-id="element-970">
              Залишіть свої дані — ми зв'яжемося з вами та допоможемо з вибором
            </p>
          </div>

          <form onSubmit={handleConsultationSubmit} className="bg-[#1a1a1a] border border-[#2a2a2a] rounded-2xl p-6 lg:p-8" data-id="element-971">
            <div className="space-y-4" data-id="element-972">
              <Input label="Ваше ім'я" value={consultationForm.name} onChange={e => setConsultationForm({
              ...consultationForm,
              name: e.target.value
            })} placeholder="Іван Петров" required data-id="element-973" />
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-974">
                <Input label="Телефон" type="tel" value={consultationForm.phone} onChange={e => setConsultationForm({
                ...consultationForm,
                phone: e.target.value
              })} placeholder="+38 (0__) ___-__-__" required data-id="element-975" />
                <Input label="Email" type="email" value={consultationForm.email} onChange={e => setConsultationForm({
                ...consultationForm,
                email: e.target.value
              })} placeholder="email@example.com" required data-id="element-976" />
              </div>
              <Button type="submit" size="lg" fullWidth data-id="element-977">
                Замовити консультацію
              </Button>
            </div>
          </form>
        </div>
      </section>
    </main>;
}